<template>
  <div class="px-4 w-full">
    <div class="flex justify-between items-center my-4">
      <h3 class="text-3xl text-blueGray-700 font-headline m-0">Contracts</h3>
      <button
        class="bg-tiara-500 hover:bg-tiara-600 transition-colors text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
        @click="toggleNewContractModal"
      >
        <i class="fa-solid fa-plus mr-1"></i> New Contract
      </button>
    </div>

    <div
      class="flex flex-col md:flex-row justify-between md:items-end mb-4 gap-4"
    >
      <div class="border-b border-tiara-300 flex-grow md:mr-4">
        <nav class="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
          <button
            :class="[
              status === 'all'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'all'"
          >
            All
          </button>
          <button
            :class="[
              status === 'pending'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'pending'"
          >
            Pending
          </button>
          <button
            :class="[
              status === 'needs_countersignature'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'needs_countersignature'"
          >
            Needs Countersignature
          </button>
          <button
            :class="[
              status === 'completed'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'completed'"
          >
            Completed
          </button>
          <button
            :class="[
              status === 'declined'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'declined'"
          >
            Declined
          </button>
          <button
            :class="[
              status === 'expired'
                ? 'border-tiara-500 text-tiara-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="status = 'expired'"
          >
            Expired
          </button>
        </nav>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div class="flex items-center space-x-2 mr-2">
          <span class="text-sm text-gray-500 font-medium whitespace-nowrap"
            >Filter by Contract:</span
          >
          <select
            v-model="templateId"
            class="border border-tiara-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-tiara-500 bg-white min-w-[150px]"
            @change="refreshSubmissions"
          >
            <option :value="null">All Contracts</option>
            <option
              v-for="template in templates"
              :key="template.id"
              :value="template.id"
            >
              {{ template.name }}
            </option>
          </select>
        </div>

        <div
          class="flex items-center space-x-2 mr-2 border border-tiara-300 px-3 py-1 rounded-lg bg-white"
        >
          <span class="text-sm text-gray-500 font-medium">Show Archived:</span>
          <slide-toggle :value="archived" @change="archived = $event" />
        </div>

        <div class="relative flex items-center group">
          <input
            v-model="q"
            type="text"
            placeholder="Search contracts..."
            class="border border-tiara-300 rounded-lg px-3 py-1.5 pr-8 text-sm outline-none focus:border-tiara-500 bg-white"
            @keyup.enter="refreshSubmissions"
          />
          <button
            v-if="q"
            class="absolute right-8 top-2 text-gray-400 hover:text-gray-600"
            @click="
              q = '';
              refreshSubmissions();
            "
          >
            <i class="fa-solid fa-times"></i>
          </button>
          <div class="ml-2 relative">
            <i
              class="fa-solid fa-circle-question text-gray-400 cursor-help hover:text-gray-600 transition-colors"
            ></i>
            <div
              class="absolute top-full right-0 mt-2 hidden group-hover:block w-64 bg-gray-800 text-white text-xs rounded shadow-lg p-2 z-50"
            >
              <p>Search is performed directly against the DocuSeal server.</p>
              <p class="mt-1">
                It matches against <strong>email addresses</strong>,
                <strong>external IDs</strong>, and <strong>metadata</strong>.
              </p>
              <p class="mt-1 text-gray-300 italic">
                Note: Searching by template name (e.g., "W9") is not supported
                by the upstream provider.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="overflow-x-auto w-full">
      <table
        class="items-center border-collapse border-2 border-solid border-tiara-300 bg-white/50 min-w-full"
      >
        <thead class="bg-tiara-200/50">
          <tr>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-left text-black/70 font-medium"
            >
              Template Name
            </th>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-left text-black/70 font-medium"
            >
              Status
            </th>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-left text-black/70 font-medium"
            >
              Created At
              <i
                class="fa-solid fa-arrow-down-short-wide text-xs text-gray-400 ml-1"
                title="Sorted descending (newest first)"
              ></i>
            </th>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-left text-black/70 font-medium"
            >
              NotedSource
            </th>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-left text-black/70 font-medium"
            >
              Other Party
            </th>
            <th
              class="px-4 align-middle py-3 whitespace-nowrap text-center text-black/70 font-medium"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <submission
            v-for="submission in submissions"
            :key="submission.id"
            :submission="submission"
            @refresh="refreshSubmissions"
          />
          <tr v-if="submissions.length === 0">
            <td colspan="6" class="text-center py-6 text-gray-500 bg-white">
              No contracts found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="pagination.next" class="flex justify-center my-4">
      <button
        class="bg-tiara-500 text-white px-3 py-1 rounded-lg"
        @click="loadSubmissions"
      >
        Load More
      </button>
    </div>
    <right-side-modal ref="newContractModal" :showing="false">
      <template #header>
        <h4 class="text-xl font-headline">New Contract</h4>
      </template>
      <template #body>
        <div class="px-5 py-5">
          <div v-if="!embedSrc">
            <div class="my-5">
              <span class="form-label">Contract</span>
              <select
                v-model="newContractTemplateId"
                class="w-full outline-none rounded border-tiara-300"
              >
                <option :value="null" disabled>Select a contract</option>
                <option
                  v-for="template in templates"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }}
                </option>
              </select>
            </div>
            <div class="my-5">
              <span class="form-label">Admin</span>
              <autocomplete-select
                v-model="admin"
                class="bg-white"
                placeholder="Admin Name"
                id-key="hashedId"
                label-key="fullName"
                :label-function="
                  function (admin) {
                    return `${admin.firstName} ${admin.lastName} (per-${admin.hashedId}, ${admin.calc_EmailAddress})`;
                  }
                "
                :filters="{ personType: 'ADMIN' }"
                autocomplete-endpoint="person"
                :initial-options="[]"
                :search-on-focus="true"
                :multiple="false"
                :row="{}"
                @input="selectAdmin($event)"
              />
            </div>
            <div v-if="isNdaTemplate" class="my-5">
              <span class="form-label">Signatory Title</span>
              <input
                v-model="signatoryTitle"
                type="text"
                placeholder="e.g. Head of Research"
                class="w-full outline-none rounded border-tiara-300 bg-white px-3 py-2"
              />
              <p class="text-xs text-gray-500 mt-1">
                The NDA's NotedSource signature block will use the selected
                admin's name and email with this title. The selected admin must
                be the one who countersigns.
              </p>
            </div>
            <div class="my-5">
              <span class="form-label">Recipient</span>
              <div class="flex items-center space-x-4 mb-3 mt-1 text-sm">
                <label class="flex items-center cursor-pointer">
                  <input
                    v-model="recipientType"
                    type="radio"
                    value="expert"
                    class="mr-2"
                  />
                  Registered Expert
                </label>
                <label class="flex items-center cursor-pointer">
                  <input
                    v-model="recipientType"
                    type="radio"
                    value="email"
                    class="mr-2"
                  />
                  External Email
                </label>
              </div>
              <autocomplete-select
                v-if="recipientType === 'expert'"
                v-model="expert"
                class="bg-white"
                placeholder="Expert Name"
                id-key="hashedId"
                label-key="fullName"
                :label-function="
                  function (expert) {
                    return `${expert.firstName} ${expert.lastName} (per-${expert.hashedId}, ${expert.calc_EmailAddress})`;
                  }
                "
                :filters="{ personType: 'EXPERT' }"
                autocomplete-endpoint="person"
                :initial-options="[]"
                :search-on-focus="true"
                :multiple="false"
                :row="{}"
                @input="selectExpert($event)"
              />
              <input
                v-else
                v-model="externalEmail"
                type="email"
                placeholder="Enter email address"
                class="w-full outline-none rounded border-tiara-300 bg-white px-3 py-2"
              />
            </div>
            <div class="flex justify-end mt-6">
              <button
                :disabled="
                  !newContractTemplateId ||
                  !admin ||
                  (isNdaTemplate && !signatoryTitle.trim()) ||
                  (recipientType === 'expert' ? !expert : !externalEmail)
                "
                class="bg-tiara-500 text-white px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
                @click="createContract"
              >
                Create Contract
              </button>
            </div>
          </div>
          <div v-else>
            <docuseal-form
              :src="embedSrc"
              :options="{ hideHeader: true }"
              @completed="completeContract"
            />
          </div>
        </div>
      </template>
    </right-side-modal>
  </div>
</template>

<script>
import { DocusealForm } from "@docuseal/vue";
import { utils } from "common-frontend";
import Submission from "../../components/Contracts/Submission.vue";
import RightSideModal from "../../components/Modals/RightSide.vue";
import AutocompleteSelect from "../../components/AutocompleteSelect.vue";

import SlideToggle from "../../components/SlideToggle.vue";

const { keysToCamel, keysToSnake } = utils;

// Mirrors NDA_TEMPLATE_SLUGS (MUTUAL_NDA, MUTUAL_NDA_V2) in api/app/extensions/docuseal.py.
const NDA_TEMPLATE_IDS = [2, 6];

export default {
  name: "Contracts",
  components: {
    Submission,
    RightSideModal,
    AutocompleteSelect,
    DocusealForm,
    SlideToggle,
  },
  data() {
    return {
      after: 0,
      submissions: [],
      templates: [],
      pagination: {},
      status: "all",
      archived: false,
      templateId: null,
      q: "",
      templateSlug: null,
      newContractTemplateId: null,
      recipientType: "expert",
      expert: null,
      externalEmail: "",
      admin: null,
      signatoryTitle: "",
      embedSrc: null,
    };
  },
  computed: {
    isNdaTemplate() {
      return NDA_TEMPLATE_IDS.includes(this.newContractTemplateId);
    },
  },
  watch: {
    status() {
      this.refreshSubmissions();
    },
    archived() {
      this.refreshSubmissions();
    },
    recipientType() {
      this.expert = null;
      this.externalEmail = "";
    },
  },
  mounted() {
    this.loadTemplates();
    this.loadSubmissions();
  },
  methods: {
    loadTemplates() {
      this.$api.axios.get(`/contract/template/`).then((res) => {
        this.templates = keysToCamel(res.data).templates.data;
      });
    },
    refreshSubmissions() {
      this.submissions = [];
      this.pagination = {};
      this.loadSubmissions();
    },
    loadSubmissions() {
      const params = {
        after: this.pagination?.next || 0,
        archived: this.archived,
        q: this.q,
      };
      if (this.status !== "all") {
        params.status = this.status;
      }
      if (this.templateId) {
        params.template_id = this.templateId;
      }
      this.$api.axios.get(`/contract/`, { params }).then((res) => {
        this.submissions.push(...keysToCamel(res.data).submissions.data);
        this.pagination = keysToCamel(res.data).submissions.pagination;
      });
    },

    toggleNewContractModal() {
      this.$refs.newContractModal.toggleModal();
    },
    selectExpert(event) {
      this.expert = event;
    },
    selectAdmin(event) {
      this.admin = event;
    },
    createContract() {
      const payload = keysToSnake({
        templateId: this.newContractTemplateId,
        expertId:
          this.recipientType === "expert" && this.expert
            ? this.expert.id
            : null,
        externalEmail:
          this.recipientType === "email" ? this.externalEmail : null,
        adminId: this.admin.id,
        signatoryTitle: this.isNdaTemplate ? this.signatoryTitle.trim() : null,
      });
      this.$api.axios.post(`/contract/`, payload).then((res) => {
        const { submission } = keysToCamel(res.data);
        this.embedSrc = submission.submitters[0].embedSrc;
        this.submissions = [];
        this.pagination = {};
        this.loadSubmissions();
      });
    },
    completeContract() {
      this.toggleNewContractModal();
      this.embedSrc = null;
      this.newContractTemplateId = null;
      this.recipientType = "expert";
      this.expert = null;
      this.externalEmail = "";
      this.admin = null;
      this.signatoryTitle = "";

      this.submissions = [];
      this.pagination = {};
      this.loadSubmissions();
    },
  },
};
</script>
